import { useState } from "react";
import { Mail, Copy, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DOC_HELP } from "@/lib/checklist";
import { labelDocument } from "@/lib/dataroom";
import { envoyerRelance } from "@/lib/dossiers";

export function buildRelance({ client, entreprise, mission, missing, notExtracted }) {
    const items = missing.map((m) => `  • ${labelDocument(m.type)} — ${DOC_HELP[m.type] || ""}`).join("\n");
    return `Objet : ${mission || "Mission d'audit"} — pièces manquantes pour ${entreprise || "votre société"}

Bonjour ${client || ""},

Dans le cadre de la mission « ${mission || ""} » portant sur ${entreprise || "la société"}, nous vous remercions de bien vouloir déposer dans la data room sécurisée les documents suivants, indispensables au démarrage de l'analyse :

${items || "  • (aucune pièce manquante)"}
${notExtracted.length ? `\nPar ailleurs, les pièces suivantes ont bien été reçues mais n'ont pas encore pu être exploitées : ${notExtracted.map((m) => labelDocument(m.type)).join(", ")}.\n` : ""}
Le lien de dépôt sécurisé vous a été communiqué précédemment ; n'hésitez pas à nous solliciter en cas de difficulté.

Nous restons à votre disposition.

Bien cordialement,
ACS Corporate — Audit & Restructuration`;
}

export default function RelanceEmailButton({ dossierId, clientEmail, client, entreprise, mission, missing, notExtracted, disabled }) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const [perso, setPerso] = useState("");
    const [sending, setSending] = useState(false);

    function generate() {
        setText(buildRelance({ client, entreprise, mission, missing, notExtracted }));
        setOpen(true);
    }

    async function copy() {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Brouillon copié dans le presse-papiers");
        } catch {
            toast.error("Copie impossible — sélectionnez le texte manuellement");
        }
    }

    async function send() {
        setSending(true);
        try {
            const r = await envoyerRelance(dossierId, perso);
            toast.success("Relance envoyée", { description: `E-mail adressé à ${r.to} (${r.pieces.length} pièce${r.pieces.length > 1 ? "s" : ""}).` });
            setOpen(false);
        } catch (e) {
            toast.error("Envoi impossible", { description: e.message });
        } finally {
            setSending(false);
        }
    }

    return (
        <>
            <Button type="button" variant="outline" data-testid="relance-email-button" onClick={generate} disabled={disabled} className="rounded-full border-primary/30 text-primary hover:bg-accent">
                <Mail className="h-4 w-4" strokeWidth={1.75} /> E-mail de relance
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="relance-dialog" className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display">E-mail de relance</DialogTitle>
                        <DialogDescription>
                            {missing.length} pièce{missing.length > 1 ? "s" : ""} obligatoire{missing.length > 1 ? "s" : ""} manquante{missing.length > 1 ? "s" : ""}. Envoi direct à <strong>{clientEmail || "— (client sans e-mail)"}</strong> avec le modèle ACS Corporate, ou copie du brouillon ci-dessous.
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea data-testid="relance-text" value={text} onChange={(e) => setText(e.target.value)} rows={12} className="bg-card text-sm font-mono" />
                    <Textarea data-testid="relance-perso" value={perso} onChange={(e) => setPerso(e.target.value.slice(0, 800))} rows={5} placeholder="Message personnalisé ajouté à l'e-mail envoyé (optionnel, 800 caractères max)" className="bg-card text-sm" />
                    <DialogFooter className="gap-2">
                        <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Fermer</Button>
                        <Button type="button" variant="outline" data-testid="relance-copy" onClick={copy} className="rounded-full"><Copy className="h-4 w-4" /> Copier le brouillon</Button>
                        <Button type="button" data-testid="relance-send" onClick={send} disabled={sending || !clientEmail || missing.length === 0} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer au client
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
